import { ButtonAPI, FieldAPI, FormAPI, InputAPI, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import moment from 'moment';
import { FragmentNames, GastherapyStartOrRestartModalAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import BaseController from '../../../../BaseController';
import timeService from '../../../../../services/time-service';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../../services/timeline-service';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { Inject } from '@odinjs/odin';
import GastherapyService, { CreateGenerateGastherapyChangeRequest, CreateGenerateGastherapyChangeResponse, GastherapyItemStatus, GastherapyProps, GastherapyStatus } from '../gastherapy-service';
import { YesNoType } from '../../../../../utils/enum-utils';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { Value } from '@philips-internal/oswald-ui-core';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ApplicationRoute } from '../../../../../constants/routes';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import MedicationService from '../../../services/medication-service';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ConsistAdministrationMarginInfo } from '../../recommendation-orders/consist-administration-margin-info';
@Controller({ fragment: FragmentNames.GASTHERAPY_START_OR_RESTART_MODAL })
export default class GastherapyExecuteTimeModalController extends BaseController {

  @Inject(GastherapyStartOrRestartModalAliases.FI_O2_FIELD)
  private fiO2Field!: FieldAPI;

  @Inject(GastherapyStartOrRestartModalAliases.FI_O2_INPUT)
  private fiO2Input!: InputAPI;

  @Inject(GastherapyStartOrRestartModalAliases.DATE_HOUR_FIELD)
  private dateHourField!: FieldAPI;

  @Inject(GastherapyStartOrRestartModalAliases.GASTHERAPY_START_OR_RESTART_FORM)
  private formApi!: FormAPI;

  @Inject(GastherapyStartOrRestartModalAliases.BUTTON_EXECUTE)
  private executeButtonApi!: ButtonAPI;

  @Inject(GastherapyStartOrRestartModalAliases.INFORMATION_FIELD)
  private informationField!: FieldAPI;

  @Inject(GastherapyStartOrRestartModalAliases.GASTHERAPY_RESPIRATORY_MODALITY)
  private gastherapyRespiratoryModality!: TextAPI;

  @Inject(GastherapyStartOrRestartModalAliases.GASTHERAPY_BREATHING_TYPE)
  private gastherapyBreathingType!: TextAPI;

  @Inject(GastherapyStartOrRestartModalAliases.GASTHERAPY_START_DEVICE_FIELD)
  private devideFiled!: FieldAPI;

  @OnMount(GastherapyStartOrRestartModalAliases.DATE_HOUR_INPUT)
  handleMountDateHourInput({ target }: EventData<InputAPI>): void {
    this.setDateVisible();
    if (this.dateHourField.isVisible()) {
      timeService.getCurrentDateTime()
        .then(date => target.setValue(date))
        .catch(() => target.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
    }
  }

  @OnMount([GastherapyStartOrRestartModalAliases.TEXT_GASTHERAPY_DATE, GastherapyStartOrRestartModalAliases.TEXT_GASTHERAPY_INFORMATION])
  handleMountTexts({ target }: EventData<TextAPI>): void {
    const data = TimelineContext.get() as TimeLineEventMedication;
    const value = target.getValue()
      .replace(':gastherapy-information', data.description as string)
      .replace(':gastherapy-date', DateFormatterUtil.timeStamp(String(data.start)));

    target.setValue(value);
  }

  @OnMount(GastherapyStartOrRestartModalAliases.FI_O2_FIELD)
  handleMountFiO2Field(): void {
    const infoGastherapy = this.context.getBulkData().gastherapy as unknown as GastherapyProps;
    const valueFIO2 = infoGastherapy.qtOxygenFraction != null ? infoGastherapy.qtOxygenFraction : '';

    this.fiO2Field.setLabel(MessageService.translate('tws.label-wire').concat(' (%)'));
    this.fiO2Input.setValue(valueFIO2);
  }

  @OnMount(GastherapyStartOrRestartModalAliases.INFORMATION_FIELD)
  handleOnMountInformationField(): void {
    const { itemStatus } = TimelineContext.get();
    this.informationField.setVisible(false);
    if (itemStatus === GastherapyItemStatus.INTERRUPTED) {
      this.viewFieldsAccordingToType();
    }
  }

  private viewFieldsAccordingToType(): void {
    this.informationField.setVisible(true);
    this.fiO2Field.setVisible(false);
    this.devideFiled.setVisible(false);
    const infoGastherapy = this.context.getBulkData().gastherapy as unknown as GastherapyProps;
    const breathingType = MessageService.translate('tws.label-respiration-type').concat(': ' + infoGastherapy.breathingType);
    const respiratoryModality = MessageService.translate('tws.label-ventilator-mod').concat(': ' + infoGastherapy.respiratoryModality);
    this.gastherapyRespiratoryModality.setValue(respiratoryModality && respiratoryModality);
    this.gastherapyBreathingType.setValue(breathingType && breathingType);
  }

  @OnMount(GastherapyStartOrRestartModalAliases.GASTHERAPY_START_DEVICE_AUTOCOMPLETE)
  handleOnMountDeviceAutocomplete(): void {
    this.fetchRespiratoryDevice().then((res) => {
      const infoGastherapy = this.context.getBulkData().gastherapy as unknown as GastherapyProps;
      const currentRecord = this.formApi.getRecord();

      for (const item of res) {
        const description = item.deviceDescription as unknown as Record<string, Value>;
        const deviceCode = item.deviceCode as unknown as Record<string, Value>;
        if (infoGastherapy.deviceDescription?.length === description.length
          || infoGastherapy.deviceCode === String(deviceCode)) {
          this.formApi.setRecord({
            ...currentRecord,
            device: {
              deviceCode: item.deviceCode,
              deviceDescription: item.deviceDescription,
            }
          })
        }
      }
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  private setDateVisible(): void {
    const displayDateField = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TO_INFORM_THE_EVENT_DATE_WHEN_STARTING_CHANGING_THE_RATE_OF_THE_GAS_THERAPY) as string
    const specifyTheEventDate = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.SPECIFY_THE_DATE_OF_THE_EVENT_WHEN_RESTARTING_INTERRUPTING_OR_SUSPENDING_THE_GASTHERAPY) === YesNoType.YES;
    const { itemStatus } = TimelineContext.get();
    const dateVisibleByCheckingParameter222 = specifyTheEventDate && itemStatus === GastherapyItemStatus.INTERRUPTED;
    const dateVisibleByCheckingParameter168 = displayDateField === YesNoType.YES && [String(GastherapyItemStatus.STARTED), GastherapyItemStatus.NOT_STARTED].includes(itemStatus);
    this.dateHourField.setVisible(dateVisibleByCheckingParameter222 || dateVisibleByCheckingParameter168);

  }

  private fetchRespiratoryDevice(): Promise<GastherapyProps[]> {
    const domainCode = 1612;
    return HttpService.get(Endpoint.EMAR_GET_RESPIRATORY_DEVICE, { domainCode: domainCode })
      .then(response => response.body as GastherapyProps[])
  }

  private setStartButtonValue(): void {
    const { itemStatus } = TimelineContext.get();
    const infoDescriptionButton = itemStatus === GastherapyItemStatus.INTERRUPTED ? 'button.infusion-viewer.restart' : 'text.execute-gastherapy'
    const scheduleTitleDescription = MessageService.translate(infoDescriptionButton);
    this.executeButtonApi.setValue(MessageService.translate(scheduleTitleDescription));
  }

  @OnMount(GastherapyStartOrRestartModalAliases.BUTTON_EXECUTE)
  handleOnClickButtonExecute(): void {
    this.setStartButtonValue();
  }

  @OnClick(GastherapyStartOrRestartModalAliases.BUTTON_EXECUTE)
  handleClickExecuteButton(): void {
    this.isLoading(true);
    const statusItem: string = TimelineContext.get().status;

    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const currentRecord = this.formApi.getRecord();
    const justification = currentRecord.justification ?? '';
    const date = this.dateHourField.isVisible()
      ? moment(currentRecord.currentTime as string).format(ISO_FULL_DATE_FORMAT)
      : moment().format(ISO_FULL_DATE_FORMAT);

    ConsistAdministrationMarginInfo.execute({
      administrationDate: date,
      itemType: PrescriptionType.GASOTERAPY,
      justification: justification as string | undefined,
      scheduleSequenceNumber: scheduleSequenceNumber as string
    }).then((response) => {
      if ((response.responseDescription) && (statusItem === String(GastherapyStatus.PENDING))) {
        this.showToast(response.responseDescription, ToastLevel.WARNING, ToastType.BANNER);
        this.isLoading(false);
      } else {
        switch (statusItem) {
          case String(GastherapyStatus.PENDING):
            this.startedGastherapy();
            break;
          case String(GastherapyStatus.INTERRUPTED):
            this.restartGastherapy();
            break;
          default:
            break;
        }
      }
    }).finally(() => {
      this.isLoading(false);
    });
  }

  private startedGastherapy(): void {
    const body = this.getBody(GastherapyItemStatus.STARTED);
    this.context.set({ actionType: GastherapyItemStatus.STARTED });
    this.startOrRestartGastherapy(body);
  }

  private restartGastherapy(): void {
    const body = this.getBody(GastherapyItemStatus.RESTARTED);
    this.context.set({ actionType: GastherapyItemStatus.RESTARTED });
    this.startOrRestartGastherapy(body);
  }

  private getBody(valueIeChange: string): Record<string, Value> {
    const timelineGastherapy = TimelineContext.get() as TimeLineEventMedication;
    const currentRecord = this.formApi.getRecord();
    const infoGastherapy = this.context.getBulkData().gastherapy as unknown as GastherapyProps;
    const date = this.dateHourField.isVisible() ?
      moment(String(currentRecord.currentTime)).format(ISO_FULL_DATE_FORMAT) : null;
    const device = currentRecord.device as GastherapyProps;
    const deviceCode = device?.deviceCode ?? infoGastherapy.deviceCode;
    const justification = currentRecord.justification != undefined ? currentRecord.justification : '';
    const quantityGastherapy = currentRecord.quantity != undefined ? currentRecord.quantity : infoGastherapy.quantity;

    return {
      device: deviceCode,
      dtChange: date,
      ieChange: String(valueIeChange),
      justification: justification,
      prescriptionNumber: timelineGastherapy.prescriptionNumber,
      qtGastherapy: quantityGastherapy,
      scheduleSeq: timelineGastherapy.id,
      seqGastherapy: timelineGastherapy.itemSequence,
      seqGastherapyCpoe: timelineGastherapy.cpoeSequence,
      unitOfMeasurement: infoGastherapy.unitOfMeasurement
    } as Record<string, CreateGenerateGastherapyChangeRequest>;
  }

  private startOrRestartGastherapy(body: Record<string, Value>): void {
    const statusItem = this.context.getBulkData();
    GastherapyService.generateGasTherapyChange(body)
      .then((res) => {
        const responseBody = res.dsMessage as CreateGenerateGastherapyChangeResponse;
        if (responseBody) {
          this.showToast(String(responseBody), ToastLevel.WARNING, ToastType.BANNER);
          return Promise.reject(false);
        }
        return Promise.resolve();
      }).then(() => {
        if (statusItem.actionType === GastherapyItemStatus.STARTED) {
          this.showToast('txt.item-started-with-success', ToastLevel.SUCCESS, ToastType.BANNER);
        } else {
          this.showToast('txt.item-restarted-with-success', ToastLevel.SUCCESS, ToastType.BANNER);
        }
        this.isLoading(false);
        this.redirectUserToTimeline();
      }).catch((err) => {
        this.informationsCatch(err);
      })
  }

  private informationsCatch(error: any): void {
    this.isLoading(false);
    const hasError = !!error;

    if (!hasError) {
      return
    }
    if (error.body?.errors) {
      error?.body?.errors.forEach((message: string) => {
        this.showToast(message, ToastLevel.WARNING, ToastType.BANNER)
      })
      return;
    }
    if (error.body) {
      this.showToast(error.body, ToastLevel.ERROR, ToastType.BANNER);
    }
  }

  private isLoading(state: boolean): void {
    this.executeButtonApi.setEnabled(!state);
    this.formApi.setLoading(state);
  }

  private showToast(message: string, toastLevel: ToastLevel, type: ToastType): void {
    const toastId = 'gastherapy-execute-time-toast';
    ToastService.close(toastId);
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: toastId,
      level: toastLevel,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type,
    });
  }

  private redirectUserToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    ModalService.close(FragmentNames.GASTHERAPY_START_OR_RESTART_MODAL);
  }
}
